/**
 * Lottie Animation Loader Service
 * Loads Lottie JSON files dynamically from public/animations folder
 * Uses caching to avoid re-fetching the same animation
 */

const animationCache = new Map<string, object>();

/**
 * Load a Lottie animation JSON file
 * @param name - Animation name without extension (e.g., "SmileAngle", "poker/Card")
 * @returns Promise resolving to the animation JSON object
 */
export async function loadAnimation(name: string): Promise<object> {
  // Check cache first
  if (animationCache.has(name)) {
    return animationCache.get(name)!;
  }

  try {
    const response = await fetch(`/animations/${name}.json`);
    if (!response.ok) {
      throw new Error(`Failed to load animation: ${name}`);
    }
    const data = await response.json();
    animationCache.set(name, data);
    return data;
  } catch (error) {
    console.error(`Error loading animation ${name}:`, error);
    throw error;
  }
}

/**
 * Preload multiple animations (useful for game views)
 * @param names - Array of animation names
 * @returns Promise resolving when all animations are loaded
 */
export async function preloadAnimations(names: string[]): Promise<void> {
  await Promise.all(names.map((name) => loadAnimation(name)));
}

/**
 * Clear animation cache (for memory management)
 */
export function clearAnimationCache(): void {
  animationCache.clear();
}

/**
 * Get animation from cache without loading
 * @param name - Animation name
 * @returns Animation data or undefined if not cached
 */
export function getCachedAnimation(name: string): object | undefined {
  return animationCache.get(name);
}

export default {
  loadAnimation,
  preloadAnimations,
  clearAnimationCache,
  getCachedAnimation,
};
